import React from 'react';
import Search from 'lucide-react/dist/esm/icons/search.js';
import Filter from 'lucide-react/dist/esm/icons/filter.js';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.js';
import Grid from 'lucide-react/dist/esm/icons/grid-3x3.js';
import List from 'lucide-react/dist/esm/icons/list.js';
import { useContent } from '../../../contexts/ContentContext';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useWinnersPage } from './WinnersPageContext';

const MONTH_KEYS = [
  '', 'month.january', 'month.february', 'month.march', 'month.april', 'month.may', 'month.june',
  'month.july', 'month.august', 'month.september', 'month.october', 'month.november', 'month.december'
];

const MONTH_FALLBACKS = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function WinnersFilterBarSection() {
  const { c } = useContent();
  const { t } = useTranslations();
  const { 
    searchTerm, 
    setSearchTerm, 
    filterYear, 
    setFilterYear,
    filterMonth,
    setFilterMonth,
    viewMode, 
    setViewMode,
    winners,
    availableYears,
    availableMonths,
  } = useWinnersPage();

  if (winners.length === 0) return null;

  return (
    <div className="rounded-xl p-6 mb-8 bg-white/5 dark:bg-gray-800/30 backdrop-blur-sm">
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex-1 w-full md:w-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('winners.filter.searchPlaceholder', 'Search by name or ticket number...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer transition-all"
            >
              <option value="all">{t('winners.filter.allYears', 'All Years')}</option>
              {availableYears.map(year => (
                <option key={year} value={String(year)}>{year}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>

          {filterYear !== 'all' && availableMonths.length > 0 && (
            <div className="relative">
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="pl-4 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer transition-all"
              >
                <option value="all">{t('winners.filter.allMonths', 'All Months')}</option>
                {availableMonths.map(month => (
                  <option key={month} value={String(month)}>{t(MONTH_KEYS[month], MONTH_FALLBACKS[month])}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          )}

          <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded transition-all ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded transition-all ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
